import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useSumOfCorrectedTotals } from '../../hooks/api/correctedTotals';
import { useStrategicVolumeReport } from '../../hooks/api/strategicVolumeReports';
import { useSumOfReleasedBiddingCalculatedTotals } from '../../hooks/api/biddingCalculated';
import type { Dayjs } from '@utils/dayjs';

const CompareTotalsWithStrategicVolume = ({
    date,
    workingUnitLocation,
}: {
    date: Dayjs;
    workingUnitLocation: number;
}) => {
    const { t } = useTranslation('bidding');

    const { sumOfCorrectedTotals } = useSumOfCorrectedTotals({
        date,
        workingUnitLocation,
    });
    const { sumOfReleasedBiddingCalculatedTotals } = useSumOfReleasedBiddingCalculatedTotals({
        date,
        workingUnitLocation,
    });
    const { strategicVolumeReport } = useStrategicVolumeReport({
        workingUnitLocation,
        date,
    });

    const sumOfTotals = useMemo((): number => {
        return sumOfCorrectedTotals !== undefined ? sumOfCorrectedTotals : sumOfReleasedBiddingCalculatedTotals || 0;
    }, [sumOfCorrectedTotals, sumOfReleasedBiddingCalculatedTotals]);

    const strategicVolumeReportValue = useMemo(() => {
        const value = strategicVolumeReport?.value;
        return value !== undefined ? parseFloat(value.toFixed(2)) : 0;
    }, [strategicVolumeReport]);

    const difference = useMemo(() => {
        const difference = -1 * (strategicVolumeReportValue - sumOfTotals);
        if (Number.isNaN(difference)) {
            return 0;
        }

        return parseFloat(difference.toFixed(2));
    }, [strategicVolumeReportValue, sumOfTotals]);

    return (
        <Grid container spacing={1} rowGap={1}>
            <Grid item xs={6}>
                <Typography>{t('field.strategicVolume')}:</Typography>
                <Typography>{strategicVolumeReportValue}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography>{t('field.sumOfIntervals')}:</Typography>
                <Typography component="span">{sumOfTotals}</Typography>
                <Typography component="span" sx={{ color: difference < 0 ? 'red' : 'green', marginLeft: '10px' }}>
                    {difference}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default CompareTotalsWithStrategicVolume;
